/**
 * Shrink a photo before it leaves the phone.
 *
 * A phone camera's 12 MP JPEG is 3-5 MB; Claude needs nothing like that to see
 * a plate, and the server refuses more than 1 MB decoded. So: decode with EXIF
 * orientation applied (or a portrait photo arrives sideways), draw into a canvas
 * whose long edge is capped, and re-encode as JPEG — retrying smaller if the
 * first pass is still too big. The photo never touches disk here or on the
 * server.
 */

export const MEAL = { edge: 1280, quality: 0.8 }
export const LABEL = { edge: 1600, quality: 0.85 }
export const MAX_BYTES = 600_000

/** Target dimensions for a long-edge cap. Never upscales. */
export function fitWithin(w: number, h: number, edge: number): { w: number; h: number } {
  const long = Math.max(w, h)
  if (long <= edge) return { w: Math.round(w), h: Math.round(h) }
  const s = edge / long
  return { w: Math.round(w * s), h: Math.round(h * s) }
}

/** Decoded size of a base64 string. */
export function base64Bytes(b64: string): number {
  const pad = b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0
  return Math.floor((b64.length * 3) / 4) - pad
}

export class ImageReadError extends Error {}

async function encode(bitmap: ImageBitmap, edge: number, quality: number): Promise<string> {
  const { w, h } = fitWithin(bitmap.width, bitmap.height, edge)
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new ImageReadError("Couldn't process that image.")
  ctx.drawImage(bitmap, 0, 0, w, h)
  const url = canvas.toDataURL('image/jpeg', quality)
  return url.slice(url.indexOf(',') + 1)
}

/** File -> base64 JPEG under MAX_BYTES. */
export async function prepareImage(file: File, preset = MEAL): Promise<string> {
  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  } catch {
    throw new ImageReadError("Couldn't read that image. Try a JPEG or PNG, or take the photo in the app.")
  }
  try {
    let b64 = await encode(bitmap, preset.edge, preset.quality)
    if (base64Bytes(b64) > MAX_BYTES) b64 = await encode(bitmap, 1024, 0.6)
    if (base64Bytes(b64) > MAX_BYTES) b64 = await encode(bitmap, 800, 0.5)
    return b64
  } finally {
    bitmap.close?.()
  }
}
